'use client';

import Script from 'next/script';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CompanyPageImages, CompanyVideo } from '../../../markupConfig';

import IconList from '@/assets/icons/iconList.svg';
import IconMap from '@/assets/icons/iconMap.svg';
import IconRoute from '@/assets/icons/iconRoute24.svg';
import { AppImageClient, AppImageData } from '@/shared/components/appImage';
import { LazyVideo } from '@/shared/components/lazyVideo';
import { classNames } from '@/shared/lib/classNames/classNames';

interface TabProps {
  pageTranslations: Record<string, string>;
  images: CompanyPageImages;
  beautifulMapsVideo: CompanyVideo;
}

interface TabContentProps {
  images: CompanyPageImages;
}

export const Tabs: React.FC<TabProps> = ({
  pageTranslations,
  images,
  beautifulMapsVideo,
}) => {
  const [activeTab, setActiveTab] = useState(1);

  const tabs = [
    {
      id: 1,
      label: pageTranslations.sectionMapTitle,
      content: <MapTab images={images} video={beautifulMapsVideo} />,
      icon: <IconMap className="h-[24px] w-[24px]" />,
    },
    {
      id: 2,
      label: pageTranslations.sectionNavigationTitle,
      content: <NavigationTab images={images} />,
      icon: <IconRoute className="h-[24px] w-[24px]" />,
    },
    {
      id: 3,
      label: pageTranslations.handbookTabLabel,
      content: <DirectoryTab images={images} />,
      icon: <IconList className="h-[24px] w-[24px]" />,
    },
  ];

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  return (
    <>
      <div className="relative mb-6 flex justify-center lg:mb-10">
        <div className="bg-surface-base-secondary flex w-full gap-4 rounded-xl p-2 lg:p-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={classNames({
                cls: 'flex w-1/3 cursor-pointer items-center justify-center gap-2 rounded-xl bg-transparent px-2 py-2 transition-all duration-200 hover:bg-white/8 lg:px-6 lg:py-3 lg:font-medium',
                mods: {
                  'bg-status-accent! text-white': activeTab === tab.id,
                  'text-white/64': activeTab !== tab.id,
                },
              })}
            >
              <div className="max-lg:hidden">{tab.icon}</div>

              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="">{activeTabContent}</div>
    </>
  );
};

const MapTab: React.FC<TabContentProps & { video: CompanyVideo }> = ({
  images,
  video,
}) => {
  const MainCard = () => (
    <div className="relative overflow-hidden rounded-[20px]">
      <LazyVideo
        src={video.src}
        poster={video.poster}
        className="h-full w-full rounded-[16px] object-cover max-lg:h-auto max-lg:w-full"
      />
      <div className="bg-surface-base-secondary absolute bottom-4 left-4 rounded-xl p-6">
        <p className="text-base">
          Наша гордость — красивая карта с полной детализацией: <br />{' '}
          от калиток до новостроек
        </p>
      </div>
    </div>
  );

  const Card: React.FC<{
    image?: AppImageData;
    videoSrc?: string;
    text: string;
  }> = ({ image, videoSrc, text }) => (
    <div className="bg-surface-base-secondary rounded-[20px] p-2 pb-5 max-lg:h-full">
      <div className="mb-5 overflow-hidden rounded-2xl">
        {image && (
          <AppImageClient {...image} alt="main" className="h-auto w-full" />
        )}

        {videoSrc && (
          <LazyVideo
            src={videoSrc}
            poster={video.poster}
            className="h-full w-full rounded-[16px] object-cover max-lg:h-auto max-lg:w-full"
          />
        )}
      </div>
      <p className="ml-3 text-base">{text}</p>
    </div>
  );

  const ModelCard = () => {
    const ModelViewer = ({ images }: TabContentProps) => (
      <>
        <div className="rounded-20 relative h-[342px] w-[560px] object-cover max-lg:h-full max-lg:w-full">
          <AppImageClient
            {...images['3dControls']}
            alt="model"
            className="absolute bottom-[20px] left-[23px] z-2 h-[24px] w-auto"
          />
          {/* @ts-ignore */}
          <model-viewer
            camera-controls=""
            shadow-intensity="1"
            src={'/models/sakurapark_all.glb'}
            touch-action="pan-x"
            ar-status="not-presenting"
            className="rounded-20 h-full w-full overflow-hidden bg-white"
            //@ts-ignore
          ></model-viewer>
        </div>

        <Script
          src="https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js"
          type="module"
        />
      </>
    );

    return (
      <div className="bg-surface-base-secondary flex items-center rounded-[20px] p-2 max-lg:h-full max-lg:flex-col lg:gap-8">
        <ModelViewer images={images} />
        <p className="text-base max-lg:mt-5 max-lg:ml-3 lg:w-1/2">
          Иммерсивные объекты — реалистичные копии объектов, даже строящихся,
          с фасадами и планировками
        </p>
      </div>
    );
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-8 max-lg:hidden">
        <div className="col-span-2">
          <MainCard />
        </div>
        <div className="col-span-1">
          <Card
            image={images.mapTab2}
            text="Много деталей: дорожки, заборы, лестницы, коттеджи и прочие объекты служат ориентирами"
          />
        </div>
        <div className="col-span-1">
          <Card
            image={images.mapTab3}
            text="Для крупных объектов — зданий, аэропортов и ТЦ —
собраны поэтажные схемы для детальной навигации"
          />
        </div>
        <div className="col-span-1">
          <Card
            image={images.mapTab4}
            text="Объекты и популярные места отмечены иконками для удобной навигации"
          />
        </div>
        <div className="col-span-1">
          <Card
            image={images.mapTab5}
            text="3D-модели зданий и транспортных узлов воссоздают объём и геометрию объектов"
          />
        </div>
        <div className="col-span-2">
          <ModelCard />
        </div>
      </div>
      <Swiper
        spaceBetween={12}
        slidesPerView="auto"
        className="px-5! py-2! lg:hidden!"
      >
        <SwiperSlide>
          <Card
            videoSrc={video.src}
            text="Наша гордость — красивая карта с полной детализацией: от калиток до новостроек"
          />
        </SwiperSlide>
        <SwiperSlide>
          <Card
            image={images.mapTab2}
            text="Много деталей: дорожки, заборы, лестницы, коттеджи и прочие объекты служат ориентирами"
          />
        </SwiperSlide>
        <SwiperSlide>
          <Card
            image={images.mapTab3}
            text="Для крупных объектов — зданий, аэропортов и ТЦ — собраны поэтажные схемы для детальной навигации"
          />
        </SwiperSlide>
        <SwiperSlide>
          <Card
            image={images.mapTab4}
            text="Объекты и популярные места отмечены иконками для удобной навигации"
          />
        </SwiperSlide>
        <SwiperSlide>
          <Card
            image={images.mapTab5}
            text="3D-модели зданий и транспортных узлов воссоздают объём и геометрию объектов"
          />
        </SwiperSlide>
        <SwiperSlide>
          <ModelCard />
        </SwiperSlide>
      </Swiper>
    </>
  );
};

const Card: React.FC<{ image: AppImageData; text: string }> = ({
  image,
  text,
}) => (
  <div className="bg-surface-base-blur rounded-20 h-full p-2 pb-5">
    <AppImageClient
      {...image}
      alt=""
      className="mb-7 h-auto w-full rounded-[20px] object-cover"
    />
    <p className="pl-2 text-base">{text}</p>
  </div>
);

const NavigationTab: React.FC<TabContentProps> = ({ images }) => (
  <>
    <div className="grid grid-cols-12 gap-8 max-lg:hidden">
      <div className="col-span-4">
        <Card
          image={images.navigationTabNew1}
          text="Для дорожного графа собираем все характеристики: от камер и светофоров до типа дорог, знаков и лимитов."
        />
      </div>
      <div className="col-span-4">
        <Card
          image={images.navigationTabNew2}
          text="Граф содержит данные обо всех видах транспорта — наземный,подземный, ж/д, с расписанием и фактическим движением."
        />
      </div>
      <div className="col-span-4">
        <Card
          image={images.navigationTabNew3}
          text="Для пешеходных маршрутов собираем информацию о тротуарах, тропах, турникетах, лестницах, заборах и калитках."
        />
      </div>
      <div className="col-span-6">
        <Card
          image={images.navigationTabNew4}
          text="Для легковых авто и грузовиков учтём пробки, знаки и платный проезд, для самокатов, велосипедов и пешеходов — лестницы, калитки и уклон."
        />
      </div>
      <div className="col-span-6">
        <Card
          image={images.navigationTabNew5}
          text="Знаем входы в здания, подъезды домов, даже номера квартир в них, чтобы экономить время и вести точно до двери."
        />
      </div>
      <div className="col-span-12">
        <div className="rounded-20 bg-surface-base-blur relative flex items-center">
          <AppImageClient
            {...images.navigationTabNew9}
            alt=""
            className="absolute bottom-0 left-0 h-full w-auto"
          />
          <div className="relative -mt-5 flex w-1/2 justify-center">
            <AppImageClient {...images.navigationTabNew6} alt="" />
          </div>

          <div className="w-1/2">
            <p>
              Проложим маршруты внутри ТЦ, офисов и стадионов и покажем,
              как быстро дойти до нужной точки на любом этаже.
            </p>
          </div>
        </div>
      </div>
      <div className="col-span-6">
        <Card
          image={images.navigationTabNew7}
          text="Каждую минуту обновляем пробки и дорожные события и прогнозируем ситуацию на дорогах по данным статистики."
        />
      </div>
      <div className="col-span-6">
        <Card
          image={images.navigationTabNew8}
          text="Вычисляем время в пути и совершенствуем алгоритмы, чтобы прогноз стал ближе к реальному времени проезда."
        />
      </div>
    </div>
    <Swiper
      spaceBetween={12}
      slidesPerView="auto"
      className="px-5! py-2! lg:hidden!"
    >
      <SwiperSlide>
        <Card
          image={images.navigationTabNew1}
          text="Для дорожного графа собираем все характеристики: от камер и светофоров до типа дорог, знаков и лимитов."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew2}
          text="Граф содержит данные обо всех видах транспорта — наземный,подземный, ж/д, с расписанием и фактическим движением."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew3}
          text="Для пешеходных маршрутов собираем информацию о тротуарах, тропах, турникетах, лестницах, заборах и калитках."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew4}
          text="Для легковых авто и грузовиков учтём пробки, знаки и платный проезд, для самокатов, велосипедов и пешеходов — лестницы, калитки и уклон."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew7}
          text="Каждую минуту обновляем пробки и дорожные события и прогнозируем ситуацию на дорогах по данным статистики."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew5}
          text="Знаем входы в здания, подъезды домов, даже номера квартир в них, чтобы экономить время и вести точно до двери."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew10}
          text="Проложим маршруты внутри ТЦ, офисов и стадионов и покажем, как быстро дойти до нужной точки на любом этаже."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.navigationTabNew8}
          text="Вычисляем время в пути и совершенствуем алгоритмы, чтобы прогноз стал ближе к реальному времени проезда."
        />
      </SwiperSlide>
    </Swiper>
  </>
);

const DirectoryTab: React.FC<TabContentProps> = ({ images }) => (
  <>
    <div className="grid grid-cols-3 gap-8 max-lg:hidden">
      <div className="col-span-1">
        <Card
          image={images.directoryTab1}
          text="Создаём для зданий не только схемы, но и собираем данные о назначении, состоянии, этажности и другие параметры."
        />
      </div>
      <div className="col-span-1">
        <Card
          image={images.directoryTab2}
          text="Собираем данные о миллионах организаций по сотням критериев: от контактов до ИНН и чека — и находим связи между компаниями"
        />
      </div>
      <div className="col-span-1">
        <Card
          image={images.directoryTab3}
          text="Знаем всё о миллионах объектов на карте: официальные и народные названия,их координаты, авторы и время создания"
        />
      </div>
    </div>
    <Swiper
      spaceBetween={12}
      slidesPerView="auto"
      className="px-5! py-2! lg:hidden!"
    >
      <SwiperSlide>
        <Card
          image={images.directoryTab1}
          text="Создаём для зданий не только схемы, но и собираем данные о назначении, состоянии, этажности и другие параметры."
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.directoryTab2}
          text="Собираем данные о миллионах организаций по сотням критериев: от контактов до ИНН и чека — и находим связи между компаниями"
        />
      </SwiperSlide>
      <SwiperSlide>
        <Card
          image={images.directoryTab3}
          text="Знаем всё о миллионах объектов на карте: официальные и народные названия,их координаты, авторы и время создания"
        />
      </SwiperSlide>
    </Swiper>
  </>
);
